import { useRef, useState } from 'react'
import { env } from '../lib/env'
import { useI18n } from '../hooks/useI18n'
import { forceSignOut, signOut, useSession } from '../hooks/useSession'
import GuestBanner from '../components/GuestBanner'
import { showToast } from '../lib/toast'
import { resolveAvatar } from '../lib/avatar'

const DEV_TAP_WINDOW_MS = 1200

/**
 * Profile — and the hidden dev section, three taps on the MEMBER label.
 *
 * Same gesture, same window, same contents as the mini program. It is reachable
 * from the GUEST card too: the states worth debugging are exactly the ones
 * where the member view is unreachable.
 */
export default function ProfilePage({
  onRegister, onEditApplication
}: { onRegister: () => void; onEditApplication: () => void }) {
  const { t, lang, toggle } = useI18n()
  const { profile, application, registered, phase } = useSession()
  const [devVisible, setDevVisible] = useState(false)
  const tapRef = useRef({ count: 0, lastAt: 0 })

  const revealDev = () => {
    if (devVisible) return
    const now = Date.now()
    const count = now - tapRef.current.lastAt <= DEV_TAP_WINDOW_MS ? tapRef.current.count + 1 : 1
    tapRef.current = { count, lastAt: now }
    console.log('[tc][dev] member label tap', count, 'of 3')
    if (count < 3) return
    tapRef.current = { count: 0, lastAt: 0 }
    setDevVisible(true)
  }

  const doForceReset = () => {
    forceSignOut()
    showToast({ title: t('profile.sessionCleared'), icon: 'none' })
  }

  if (!registered) {
    return (
      <div>
        <button type="button" onClick={revealDev} className="block py-2 text-2xs uppercase tracking-kicker text-gold">
          {t('profile.member')}
        </button>
        <div className="rounded-md border border-gold-dim bg-card px-[18px] py-4">
          <GuestBanner variant={phase === 'incomplete' ? 'incomplete' : 'profile'} onRegister={onRegister} />
          {devVisible ? <DevRows onForceReset={doForceReset} onSignOut={signOut} phase={phase} /> : null}
        </div>
      </div>
    )
  }

  const answers = (application?.answers || {}) as Record<string, unknown>
  const lookingFor = Array.isArray(answers.looking_for) ? (answers.looking_for as string[]) : null
  const completion = typeof application?.completion_percentage === 'number'
    ? application.completion_percentage
    : null

  return (
    <div>
      <button type="button" onClick={revealDev} className="block py-2 text-2xs uppercase tracking-kicker text-gold">
        {t('profile.member')}
      </button>

      <div className="flex items-center gap-3 rounded-md border border-gold-dim bg-card px-[18px] py-4">
        {profile?.avatar_url
          ? <img src={resolveAvatar(profile.avatar_url)} alt="" className="h-14 w-14 rounded-full object-cover" />
          : <div className="flex h-14 w-14 items-center justify-center rounded-full bg-card-2 text-h2 text-gold">
              {(profile?.display_name || '·').trim().charAt(0).toUpperCase()}
            </div>}
        <div className="min-w-0">
          <p className="truncate text-lg text-ivory">{profile?.display_name || profile?.username}</p>
          {/* `phone_number` is already E.164 — it CARRIES the +86. Prefixing
              `phone_country_code` on top of it produced "+86 +8613120581428".
              Format the one stored value; never concatenate the two. */}
          {profile?.phone_number
            ? <p className="text-sm text-muted">{formatPhone(String(profile.phone_number))}</p>
            : null}
        </div>
      </div>

      {/* ---- My application — the card the mini program has and this did not ---- */}
      <p className="mt-5 text-2xs uppercase tracking-kicker text-gold">{t('profile.app')}</p>
      <div className="mt-2 rounded-md border border-gold-dim bg-card px-[18px] py-4">
        <Row label={t('profile.status')} value={application?.status || '—'} gold />
        {lookingFor?.length
          ? <Row label={t('profile.herefor')} value={lookingFor.map(humanize).join(', ')} />
          : null}
        {completion !== null ? (
          <div className="pt-2">
            <p className="text-xs text-muted">{t('profile.completion')}</p>
            <div className="mt-1.5 h-0.5 w-full rounded-pill bg-gold-faint">
              <div className="h-full rounded-pill bg-gold" style={{ width: `${Math.min(100, completion)}%` }} />
            </div>
          </div>
        ) : null}
        <div className="my-3 h-px bg-gold-dim" />
        <Row label={t('profile.update')} onClick={onEditApplication} gold />
      </div>

      {/* ---- Membership ---- */}
      <p className="mt-5 text-2xs uppercase tracking-kicker text-gold">{t('profile.membership')}</p>
      <div className="mt-2 rounded-md border border-gold-dim bg-card px-[18px] py-4">
        <Row label={t('profile.invited')} value={(profile?.invited_by as string) || '—'} />
        <Row label={t('profile.attended')} value={String(profile?.dinners_attended ?? 0)} />
        <Row label={t('profile.meetAgain')} value={t('profile.high')} gold />
        <Row label={t('profile.referrals')} value={t('profile.gift')} />
      </div>

      {/* ---- Language ---- */}
      <p className="mt-5 text-2xs uppercase tracking-kicker text-gold">{t('profile.lang')}</p>
      <div className="mt-2 rounded-md border border-gold-dim bg-card px-[18px] py-4">
        <Row
          label={t('profile.lang')}
          value={`${lang === 'zh' ? '中文' : 'English'} · ${lang === 'zh' ? 'EN →' : '中文 →'}`}
          onClick={toggle}
          gold
        />
      </div>

      <p className="mt-5 text-2xs uppercase tracking-kicker text-gold">{t('profile.session')}</p>
      <div className="mt-2 rounded-md border border-gold-dim bg-card px-[18px] py-4">
        <Row label={t('profile.logout')} onClick={signOut} danger />
        {devVisible ? <DevRows onForceReset={doForceReset} onSignOut={signOut} phase={phase} /> : null}
      </div>
    </div>
  )
}

/** +8613120581428 -> +86 131 2058 1428. Never prepend a country code to this. */
function formatPhone(e164: string): string {
  const china = /^\+86(\d{3})(\d{4})(\d{4})$/.exec(e164)
  if (china) return `+86 ${china[1]} ${china[2]} ${china[3]}`
  return e164
}

/** `business_network` -> `Business network`, for answer values shown raw. */
const humanize = (value: string) =>
  value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())

function DevRows({
  onForceReset, onSignOut, phase
}: { onForceReset: () => void; onSignOut: () => void; phase: string }) {
  const { t } = useI18n()
  return (
    <>
      <p className="mt-4 text-2xs uppercase tracking-kicker text-gold">{t('profile.devMode')}</p>
      <div className="mt-1 space-y-1">
        <Row label={t('profile.buildVersion')} value={env.devMode ? 'dev (fixtures)' : 'web'} />
        <Row label="Phase" value={phase} />
        <Row label="API" value={env.apiUrl || '—'} />
        {phase === 'incomplete' ? <Row label={t('profile.logout')} onClick={onSignOut} danger /> : null}
        <Row label={t('profile.forceSignOut')} onClick={onForceReset} danger />
      </div>
    </>
  )
}

function Row({
  label, value, onClick, danger = false, gold = false
}: { label: string; value?: string; onClick?: () => void; danger?: boolean; gold?: boolean }) {
  const tone = danger ? 'text-[#e08a6a]' : gold ? 'text-gold' : 'text-ivory'
  const content = (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <span className={`text-sm ${danger || gold ? tone : 'text-muted'}`}>{label}</span>
      <span className={`truncate text-sm ${tone}`}>{value ?? '→'}</span>
    </div>
  )
  return onClick ? <button type="button" onClick={onClick} className="w-full text-left">{content}</button> : content
}
